/**
 * Profile data needed to determine user availability.
 * Only includes the fields relevant for availability checks.
 */
export interface UserAvailabilityProfile {
  user_id: string;
  is_disabled?: boolean | null;
  azure_disabled_at?: string | null;
}

/**
 * Creates a function to check if a user is unavailable (deleted, disabled, or Azure-disabled).
 *
 * A user is considered unavailable if:
 * - Their profile doesn't exist in the provided map (deleted)
 * - Their `is_disabled` flag is true (manually disabled)
 * - Their `azure_disabled_at` is set (disabled via Azure AD sync)
 *
 * @param profilesMap - Map of user_id to profile data, or Record<string, profile>
 * @returns A function that checks if a given userId is unavailable
 */
export function createIsUserUnavailable<T extends UserAvailabilityProfile>(
  profilesMap: Map<string, T> | Record<string, T>,
): (userId: string | null) => boolean {
  const isMap = profilesMap instanceof Map;

  return (userId: string | null): boolean => {
    if (!userId) return false;

    const profile = isMap
      ? (profilesMap as Map<string, T>).get(userId)
      : (profilesMap as Record<string, T>)[userId];

    // User is unavailable if: no profile found, is_disabled, or azure_disabled_at is set
    // Use !! to handle both null and undefined as "not set"
    return !profile || profile.is_disabled === true || !!profile.azure_disabled_at;
  };
}
